import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Checkbox, useSimpleInputState } from "capitalrx-components-2.0/lib/forms";
import { Panel, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Subheading, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { useMedicarePrescriptionPaymentPlanParticipateMutation } from "@/App/api/medicarePrescriptionPaymentPlan";
import { ParticipationStatus, ThemedButton } from "@/App/components";
import { useIsLoggedInUser, useIsMemberPrecludedFromParticipation, useNavigateClient } from "@/App/hooks";
import { usePlanYear } from "@/App/pages/MedicarePrescriptionPaymentPlan/PlanYearContext";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { getUsersTimezone } from "@/App/utils/dates";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.optInForm";

export const OptInForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useSimpleInputState<boolean>(false);

  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();
  const { isLoggedInUser } = useIsLoggedInUser();
  const showAlert = useAlert();
  const planYear = usePlanYear();

  const { mutateAsync: participate } = useMedicarePrescriptionPaymentPlanParticipateMutation();
  const {
    isBeforeEffectiveDate,
    isPrecludedByPlan,
    isInvolutarilyDisenrolled,
    isMemberTerminated,
    isPlanYearNotOpen,
  } = useIsMemberPrecludedFromParticipation(planYear);

  const handleParticipateClick = async () => {
    setIsLoading(true);
    logEvent(ANALYTIC_EVENTS.MPPP.OPT_IN_FORM.optIn);
    try {
      await participate({
        tz: getUsersTimezone(),
        plan_year: planYear,
      });
      showAlert({
        message: translate(`${TRANSLATION_PATH}.optedInSuccessfully`),
        type: "success",
        style: "toast",
      });
    } catch (err) {
      showAlert({
        message: typeof err === "string" ? err : err.response?.data?.message || err.data?.message || err.message,
        type: "error",
        style: "toast",
      });
    } finally {
      setIsLoading(false);
      navigate(`/medicare-prescription-payment-plan?tab=participation-${planYear}`);
    }
  };

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      tablet: false,
      desktop: false,
    },
  });

  const StackOrShelf = isCompressed ? Stack : Shelf;

  return (
    <>
      <Panel>
        <Stack spacing="large">
          <Stack spacing="medium">
            <Subtitle>{translate(`${TRANSLATION_PATH}.medicarePrescriptionPaymentPlan`)}</Subtitle>
            <Stack spacing="large">
              {!isPrecludedByPlan && !isInvolutarilyDisenrolled && !isMemberTerminated && !isBeforeEffectiveDate && (
                <Body>{translate(`${TRANSLATION_PATH}.optInHelpText`)}</Body>
              )}
              <Shelf testId="participation-status" spacing="small">
                <Body>{translate(`${TRANSLATION_PATH}.participationStatus`)}</Body>
                <ParticipationStatus planYear={planYear} />
                <Shelf.Spacer />
              </Shelf>
              {(isPrecludedByPlan || isInvolutarilyDisenrolled || isMemberTerminated || isBeforeEffectiveDate) && (
                <Body>{translate(`${TRANSLATION_PATH}.notEligibleText`)}</Body>
              )}
            </Stack>
          </Stack>

          {!isPrecludedByPlan && !isInvolutarilyDisenrolled && !isMemberTerminated && !isBeforeEffectiveDate && (
            <Stack spacing="medium">
              <Heading>{translate(`${TRANSLATION_PATH}.optIn`)}</Heading>

              <Body style={{ marginBottom: 8 }}>{translate(`${TRANSLATION_PATH}.reviewTerms`)}</Body>

              <Subheading>{translate(`${TRANSLATION_PATH}.termsAndConditions`)}</Subheading>

              <Body>
                <Trans
                  i18nKey={`${TRANSLATION_PATH}.termsAndConditionsForMedicarePrescriptionPaymentPlan`}
                  components={{
                    PrivacyPolicyLink: (
                      <a href="https://www.cap-rx.com/legal#legal-notice-privacypolicy" target="_blank" rel="noopener noreferrer">
                        https://www.cap-rx.com/legal#legal-notice-privacypolicy
                      </a>
                    ),
                  }}
                />
              </Body>

              <Stack spacing="medium">
                <Checkbox
                  testId="terms-and-conditions"
                  value={acceptedTerms}
                  label={translate(`${TRANSLATION_PATH}.termsAndConditionsAgreeText`)}
                  hideLabel
                  onChange={setAcceptedTerms}
                />
                <StackOrShelf spacing="small">
                  <ThemedButton
                    appearance="primary"
                    testId="opt-in"
                    disabled={
                      !isLoggedInUser
                      || isLoading
                      || isPrecludedByPlan
                      || isInvolutarilyDisenrolled
                      || isMemberTerminated
                      || isBeforeEffectiveDate
                      || isPlanYearNotOpen
                    }
                    onClick={() => {
                      if (!acceptedTerms) {
                        showAlert({
                          message: translate(`${TRANSLATION_PATH}.termsAndConditionsNotAgreedError`),
                          type: "error",
                          style: "toast",
                        });
                        return;
                      }
                      handleParticipateClick();
                    }}
                  >
                    {translate(`${TRANSLATION_PATH}.optIn`)}
                  </ThemedButton>
                  <ThemedButton testId="opt-in-cancel" appearance="secondary" onClick={() => window.history.back()}>
                    {translate(`${TRANSLATION_PATH}.cancel`)}
                  </ThemedButton>
                  <StackOrShelf.Spacer />
                </StackOrShelf>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Panel>
    </>
  );
};
